import type { RoomId, StructuralElementId, UnitPositionId, WallId } from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import { GEOMETRY_DECIMALS, roomPerimeter, type Room } from './room';
import {
  isValidOutline,
  polygonArea,
  polygonEdges,
  polygonPerimeter,
  segmentLength,
  type Segment,
} from './polygon';
import type { EdgeWithoutWall } from './room-edge';
import {
  floorOpeningsWidth,
  isExternal,
  isPartyWall,
  areaOfOpening,
  contourOfOpening,
  openingsArea,
  widthOfOpening,
  wallGrossArea,
  wallNetArea,
  wallVolume,
  type Opening,
  type OpeningKind,
  type Wall,
} from './wall';
import {
  elementBottom,
  elementEspala,
  elementFaces,
  elementRun,
  elementVolume,
  espalaFaces,
  segmentFaceArea,
  segmentShadowArea,
  VOLUME_DECIMALS,
  type ElementSegment,
  type StructuralElement,
  type StructuralElementKind,
} from './structural-element';
import {
  countedSideMargin,
  serviceElement,
  type AppliesTo,
  type WithSideMargin,
} from './applies-to';
import { NO_SHAFTS, shaftArea, shaftContour, type Shaft, type ShaftTotals } from './shaft';
import type { PieceMark, PileType, StructuralPiece } from './pile';
import type { GroundBeam, PileCap } from './foundation';
import { beamDepthBelowSlab, type Slab, type SlabOpening } from './slab';
import { NO_SLABS, scaleSlabTotals, slabTotals, type SlabTotals } from './slab-totals';
import type { RetainingColumn, RetainingWall } from './retaining';
import {
  NO_RETAINING_COLUMNS,
  NO_RETAINING_WALLS,
  retainingColumnTotals,
  retainingWallTotals,
  scaleRetainingColumnTotals,
  scaleRetainingWallTotals,
  type RetainingColumnTotals,
  type RetainingWallTotals,
} from './retaining-totals';
import type { Stair } from './stair';
import { NO_STAIRS, scaleStairTotals, stairTotals, type StairTotals } from './stair-totals';
import {
  groundBeamTotals,
  NO_GROUND_BEAMS,
  NO_PILE_CAPS,
  pileCapTotals,
  scaleGroundBeamTotals,
  scalePileCapTotals,
  type GroundBeamTotals,
  type PileCapTotals,
} from './foundation-totals';
import {
  NO_PILES,
  pileSteelTotal,
  pileTotals,
  scalePileTotals,
  type PileTotals,
} from './pile-totals';

/**
 * A conta da tipologia — função **pura**, sem persistência.
 *
 * Recebe ambientes, paredes e vãos já carregados e devolve as áreas. É ela que responde a
 * pergunta que o engenheiro faria no papel: quantos metros de alvenaria, de reboco, de
 * piso e de forro tem este apto.
 *
 * Duas regras moram aqui e não têm exceção:
 *
 * - **alvenaria conta a parede uma vez** — e a divisa entre dois aptos entra pela metade
 *   em cada um (RN-163);
 * - **revestimento conta a face** — e cada face vai para o ambiente que ela encara,
 *   mesmo quando a parede é do vizinho (RN-178). É o que mantém o reboco do hall dentro
 *   da área comum, com a parede pertencendo ao apto.
 *
 * A altura também é duas: a parede tem a estrutural, até a laje; a face tem o pé-direito
 * do ambiente (RN-162). Gesso rebaixado encurta a pintura, não a alvenaria.
 */

/**
 * Como o serviço mede — decide qual das áreas abaixo ele consome.
 *
 * `MODEL` é a exceção que confirma a regra: os outros dizem qual grandeza do
 * **desenho** o serviço consome, e o desenho responde. O serviço que veio de um modelo
 * IFC já chegou com a quantidade dentro — a viga informou os próprios metros cúbicos —,
 * e o traçado não tem nada a dizer sobre ele. Usar `UNIT` para isso confundiria "veio do
 * modelo" com "é decisão de projeto", que são coisas diferentes na tela de conferência.
 *
 * Desde a Fase 24 a medida é só a **grandeza**; a peça de onde ela sai é o `AppliesTo` do
 * serviço (`applies-to.ts`). Volume e espala servem ao pilar e à viga, e o serviço diz qual.
 */
export type MeasureBy =
  | 'WALL'
  | 'FACE'
  | 'FLOOR'
  | 'CEILING'
  | 'PERIMETER'
  | 'SKIRTING'
  | 'ESPALA'
  | 'VOLUME'
  | 'UNIT'
  | 'MODEL'
  // A matriz completa (Fase 24, fatia 10). A grandeza é a mesma em vários elementos — a
  // contagem é de parede, de pilar, de janela, do guarda-corpo (fatia 9) e do shaft (fatia
  // 11) —, e quem diz qual é o `AppliesTo` (ADR-148).
  | 'COUNT'
  | 'LENGTH'
  | 'AREA'
  | 'BOTTOM'
  | 'CONTOUR'
  | 'WIDTH'
  // O aço da peça, em kg (Fase 27, F5 · ADR-176): o volume da peça vezes a taxa de aço da
  // disciplina. É grandeza como as outras, e não índice de um item, para que o CORTE E DOBRA,
  // a MONTAGEM e a INSTALAÇÃO DE FERRAGEM leiam o mesmo kg — e só o primeiro compre (RN-366).
  | 'ACO'
  // As estacas marcadas na planta **para este serviço** (Fase 27, D4 · RN-365): o teste de
  // resistência e o controle tecnológico contam cada um as suas. É da estaca, e de mais nenhuma
  // peça — por isso o elemento é implícito (`impliedElement`).
  | 'MARKED'
  // O fundo mais a borda da laje, num número só (Fase 27, etapa 3, D7): a fôrma dela é um
  // serviço, e não dois — a área do contorno somada ao perímetro × espessura. É da laje, e de
  // mais nenhuma peça — por isso o elemento é implícito (`impliedElement`).
  | 'AREA_CONTOUR';

/**
 * A grandeza pelo nome, sem dizer de que peça — o rótulo da grandeza sozinha.
 *
 * Com o elemento ao lado ela ganha o texto do par (`measureLabel`, em `applies-to.ts`): "faces"
 * é a soma dos dois lados na parede, quatro faces no pilar e três na viga, e nenhum texto só
 * dá conta dos três. Este aqui é o que sobra quando não se sabe a peça — e a lista que o
 * contrato valida (`Object.keys`).
 */
export const MEASURE_BY_LABEL: Readonly<Record<MeasureBy, string>> = {
  WALL: 'Área de parede (conta uma vez)',
  FACE: 'Faces (área das faces da peça)',
  FLOOR: 'Área de piso',
  CEILING: 'Área de teto',
  PERIMETER: 'Perímetro (cheio)',
  SKIRTING: 'Rodapé (perímetro menos as portas)',
  ESPALA: 'Espala (quina da peça, em metro linear)',
  VOLUME: 'Volume',
  UNIT: 'Unidade',
  MODEL: 'Vem do modelo (IFC)',
  COUNT: 'Contagem',
  LENGTH: 'Comprimento',
  // Do vão, do guarda-corpo (fatia 9) e do shaft (fatia 11) — a conta de cada um está no
  // texto do par.
  AREA: 'Área da peça',
  BOTTOM: 'Área de fundo (largura × comprimento)',
  // O vão da parede e o da laje (o shaft): batente, requadro.
  CONTOUR: 'Contorno do vão (batente, requadro)',
  WIDTH: 'Largura somada (soleira, peitoril)',
  ACO: 'Aço da peça (volume × taxa de aço da disciplina)',
  MARKED: 'Estacas marcadas',
  AREA_CONTOUR: 'Fundo + borda',
};

/**
 * De onde saiu a área do piso — a tela mostra, porque as três merecem confiança diferente.
 */
export type AreaSource =
  /** Alguém digitou. Vence tudo (RN-157). */
  | 'CORRIGIDA'
  /** Saiu do contorno traçado sobre a planta — exata, para qualquer forma. */
  | 'CONTORNO'
  /** Saiu das paredes do ambiente: quatro paredes que fecham um retângulo. */
  | 'PAREDES'
  /** Saiu de largura × comprimento, porque as paredes não fecham forma conhecida. */
  | 'MEDIDAS';

export interface RoomGeometry {
  readonly roomId: RoomId;
  readonly floorArea: number;
  readonly ceilingArea: number;
  /**
   * Quanto os shafts tiraram do piso e do teto deste ambiente (RN-271). Ausente é nenhum.
   *
   * Sai ao lado, e não só dentro do piso, porque o piso que encolheu sem explicação é o
   * número que alguém corrige à mão — e corrigido, o buraco voltaria a ser laje.
   */
  readonly shaftArea?: number;
  /**
   * Soma dos comprimentos das paredes que cercam o ambiente.
   *
   * Sai das **paredes**, e não de 2 × (largura + comprimento): quem acrescenta uma parede
   * ou corrige um comprimento está redesenhando o contorno, e o rodapé tem de acompanhar.
   * Ambiente ainda sem parede nenhuma cai no retângulo das medidas.
   */
  readonly perimeter: number;
  /**
   * O perímetro que leva rodapé — o cheio menos o que o interrompe.
   *
   * Sai separado de `perimeter` porque as duas medidas lineares da obra não são a mesma:
   * a soleira **é** a porta e o peitoril **é** a janela, então esses dois querem o
   * perímetro cheio; o rodapé é o que corre entre eles, e não atravessa porta nem parede
   * conjugada. Um número só para os três daria a um deles a medida do outro.
   */
  readonly skirtingLength: number;
  /** Faces de parede que encaram este ambiente, já sem os vãos (RN-178). */
  readonly faceArea: number;
  readonly areaSource: AreaSource;
  /** Quantas paredes cercam este ambiente — a tela explica de onde veio o número. */
  readonly wallCount: number;
  /** O ar do ambiente: o piso × o pé-direito, em m³ (fatia 10). */
  readonly volume: number;
}

/** Quanto desta parede é de cada apto. Divisa dá duas partes; o resto dá uma. */
export interface WallShare {
  readonly positionId?: UnitPositionId;
  readonly fraction: number;
  readonly area: number;
}

export interface WallGeometry {
  readonly wallId: WallId;
  readonly grossArea: number;
  readonly openingArea: number;
  /** A sombra do pilar e da viga neste plano — o bloco que a estrutura tirou (RN-182). */
  readonly structureArea: number;
  /** Alvenaria — zero quando a parede é conjugada, porque ali não há parede. */
  readonly netArea: number;
  /** O comprimento — zero na conjugada, pelo mesmo motivo da alvenaria (fatia 10). */
  readonly length: number;
  /** A área líquida × a espessura, em m³. Zero sem espessura cadastrada (fatia 10). */
  readonly volume: number;
  readonly shares: readonly WallShare[];
}

/** Quanto do concreto de um elemento é de cada apto. Divisa dá duas partes; o resto, uma. */
export interface VolumeShare {
  readonly positionId?: UnitPositionId;
  readonly fraction: number;
  readonly volume: number;
}

/**
 * Quanto da espala de um elemento é de cada apto — e **não** se rateia (RN-178).
 *
 * O concreto é a peça, e a peça em cima da divisa é dos dois: meio a meio. A espala é o
 * arremate de uma **face**, e face tem dono — a do pilar que avança para dentro do apto 01
 * é vista do apto 01, e o apto 02 não paga arremate que não enxerga. É a mesma distinção
 * que a Fase 14 já fez entre alvenaria e revestimento na parede de divisa.
 *
 * Sem `positionId` é a espala que ainda não pertence a apto nenhum — a da tipologia
 * montada sozinha —, ou a que dá para a rua, que é da fachada.
 */
export interface EspalaShare {
  readonly positionId?: UnitPositionId;
  /** Quantas faces do elemento este lado vê. A corrida é uma só, e multiplica (RN-190). */
  readonly faces: number;
  readonly length: number;
}

export interface ElementGeometry {
  readonly elementId: StructuralElementId;
  /** A corrida que valeu: altura no pilar, soma dos trechos na viga (fatia 4). */
  readonly run: number;
  /** O concreto do elemento inteiro, antes de qualquer rateio. */
  readonly volume: number;
  readonly shares: readonly VolumeShare[];
  /** O metro linear de espala do elemento inteiro, antes de saber de quem é (RN-187). */
  readonly espala: number;
  /** De quem é a espala — do lado que vê a face. */
  readonly espalaShares: readonly EspalaShare[];
  /** As quatro faces do pilar, as três da viga, em m² — sempre, onde quer que esteja (fatia 10). */
  readonly faceArea: number;
  /** A área de fundo da viga, em m². Zero no pilar (fatia 10). */
  readonly bottomArea: number;
  /**
   * A medida da peça **nesta conta**, em cada grandeza que o pilar e a viga medem (Fase 42,
   * RN-564): a parte desta tipologia, como os totais por elemento a somam — o concreto e a espala
   * pelo rateio da divisa, as faces, o fundo, a corrida e a contagem pela fração da peça. É o que
   * reparte o serviço peça a peça, pelo fator de cada uma; a soma das peças é o total do serviço.
   */
  readonly ownMeasures: Readonly<Partial<Record<MeasureBy, number>>>;
}

/**
 * O que os vãos de um tipo somam — a porta, a janela e a passagem têm um cada (fatia 10).
 *
 * Quatro números porque são quatro perguntas de obra: quanto de vão (`area`), quantos (`count`),
 * quanto de batente ou requadro (`contour`) e quanta soleira ou peitoril (`width`). Todos já
 * multiplicam o `count` do registro: duas janelas iguais num cartão são duas.
 */
export interface OpeningTotals {
  readonly area: number;
  readonly count: number;
  readonly contour: number;
  readonly width: number;
}

/**
 * O guarda-corpo do desenho, medido como se compra (Fase 24, D25).
 *
 * Três números porque a obra o compra de três jeitos: o metálico por metro, o de vidro por
 * metro quadrado, e o serviço de instalação por peça. Entram o do lado sem parede e o de
 * cima da mureta — os dois são o mesmo guarda-corpo, preso em lugares diferentes (RN-268).
 */
export interface RailingMeasure {
  /** Metros. */
  readonly length: number;
  /** Altura × comprimento, em m². */
  readonly area: number;
  /** Uma peça por lado, ou por mureta. */
  readonly count: number;
}

export interface TakeoffGeometry {
  readonly rooms: readonly RoomGeometry[];
  readonly walls: readonly WallGeometry[];
  /** Alvenaria total, contada uma vez. */
  readonly wallArea: number;
  /** Fachada: a face das paredes externas que dá para a rua, sem dono entre os ambientes. */
  readonly outsideFaceArea: number;
  /**
   * Concreto de pilar e viga, em m³ — somado por **elemento**, nunca por trecho (RN-190).
   *
   * É o concreto **do desenho**, para conferência. Serviço nenhum o consome: quem mede é
   * `volumeByKind`, porque pilar e viga nunca se misturam num serviço (RN-257).
   */
  readonly volume: number;
  /** O mesmo concreto, separado por peça — é daqui que o serviço mede (RN-257). */
  readonly volumeByKind: Readonly<Record<StructuralElementKind, number>>;
  /** Espala de pilar e viga, em metro linear — também por elemento (RN-187, RN-190). */
  readonly espalaLength: number;
  /** A mesma espala, separada por peça (RN-257). */
  readonly espalaByKind: Readonly<Record<StructuralElementKind, number>>;
  /** Cada pilar e cada viga do desenho, com a corrida que valeu e de quem é o concreto. */
  readonly elements: readonly ElementGeometry[];
  /** O guarda-corpo — dos lados sem parede e de cima das muretas (RN-268). */
  readonly railing: RailingMeasure;
  /** Os shafts dos ambientes desta conta, somados: área, requadro e quantos (RN-270). */
  readonly shaft: ShaftTotals;
  /** As estacas posicionadas desta conta: contagem, volume, comprimento, aço e marcadas (RN-364). */
  readonly pile: PileTotals;
  /** Os blocos desta conta: contagem, volume, faces e fundo (Fase 27, etapa 2). */
  readonly pileCap: PileCapTotals;
  /** As vigas baldrame desta conta, pelo trecho livre — o que passa dentro de bloco sai (RN-368). */
  readonly groundBeam: GroundBeamTotals;
  /** O radier desta conta: contagem, área, volume, borda e fundo + borda (Fase 27, etapa 3). */
  readonly raft: SlabTotals;
  /** A laje desta conta, sem os shafts que caem nela (RN-370). */
  readonly slab: SlabTotals;
  /** O muro de contenção desta conta, sem os pilares encaixados (RN-372). */
  readonly retainingWall: RetainingWallTotals;
  /** O pilar de contenção desta conta, sem as faces dentro do muro (RN-372). */
  readonly retainingColumn: RetainingColumnTotals;
  /** A escada de concreto desta conta — peça própria, fora do ambiente ESCADA (F17). */
  readonly stair: StairTotals;
  /** Paredes que apontam ambiente inexistente — registradas e ignoradas, não derrubam a conta. */
  readonly orphanWallIds: readonly WallId[];
  /** Elementos cujos trechos apontam paredes que não estão no desenho — mesmo tratamento. */
  readonly orphanElementIds: readonly StructuralElementId[];
  /** O comprimento das paredes que existem, na parte que é desta tipologia (fatia 10). */
  readonly wallLength: number;
  /** O volume das paredes: área líquida × espessura (fatia 10). */
  readonly wallVolume: number;
  /** Quantas paredes existem — a divisa conta meia em cada apto, como a alvenaria (fatia 10). */
  readonly wallCount: number;
  /** As faces de pilar e de viga, por peça — nunca somadas (RN-257, RN-261). */
  readonly facesByKind: Readonly<Record<StructuralElementKind, number>>;
  /** A área de fundo da viga. O pilar não tem. */
  readonly bottomByKind: Readonly<Record<StructuralElementKind, number>>;
  /** A corrida: a altura somada dos pilares, o comprimento somado das vigas. */
  readonly runByKind: Readonly<Record<StructuralElementKind, number>>;
  /** Quantos pilares e quantas vigas — a peça em cima da divisa conta meia em cada apto. */
  readonly countByKind: Readonly<Record<StructuralElementKind, number>>;
  /** O que cada tipo de vão soma. Vão de parede conjugada ou órfã não entra: a parede não existe. */
  readonly openingsByKind: Readonly<Record<OpeningKind, OpeningTotals>>;
}

export interface ComputeGeometryInput {
  readonly rooms: readonly Room[];
  readonly walls: readonly Wall[];
  readonly openings?: readonly Opening[];
  /**
   * Quais posições do pavimento tipo são área comum.
   *
   * Existe porque o rateio depende de quem está do outro lado (RN-163): divisa entre dois
   * aptos vai meio a meio, mas a parede entre o apto e o hall é **inteira do apto**. Sem
   * saber qual posição é a área comum, as duas pareceriam a mesma coisa.
   */
  readonly commonAreaPositionIds?: readonly UnitPositionId[];
  /**
   * Ambientes que **dão lado** a uma parede sem entrar no total.
   *
   * É o vizinho do outro lado da divisa, quando a conta é de uma tipologia só. A parede
   * precisa dele para não virar órfã — e o piso, o teto e o perímetro dele são da
   * tipologia dele, não desta. Sem esta separação a divisa entre dois apartamentos ou
   * teria de sumir da conta, ou traria o apartamento vizinho junto.
   */
  readonly neighbourRooms?: readonly Room[];
  /**
   * Quais posições contam como "minhas" na hora de somar alvenaria.
   *
   * Existe por causa da divisa: a parede é **uma**, e cada lado leva a sua parte
   * (RN-163) — meio a meio entre dois aptos, inteira do apto quando o vizinho é área
   * comum. Ausente = tudo é meu, que é o caso de quem calcula uma tipologia fechada em si
   * e continua sendo o caminho da maioria das telas.
   */
  readonly ownPositionIds?: readonly UnitPositionId[];
  /**
   * Pilar e viga do desenho (Fase 15).
   *
   * Ausente é o desenho que ainda não cadastrou estrutura nenhuma — e é como toda
   * tipologia da Fase 14 continua contando, com a parede inteira.
   */
  readonly elements?: readonly StructuralElement[];
  /** Onde cada elemento cruza cada parede. É o trecho que desconta (ADR-115). */
  readonly elementSegments?: readonly ElementSegment[];
  /**
   * Os buracos na laje (Fase 24, D21). Cada um desconta do piso e do teto do ambiente dele
   * (RN-271); o do ambiente que não está nesta conta — o do vizinho — fica de fora, porque é
   * da tipologia dele.
   */
  readonly shafts?: readonly Shaft[];
  /**
   * As peças da fundação desta tipologia — por enquanto, as estacas (Fase 27, RN-361).
   *
   * Só a **posicionada** conta (RN-364), e a de tipo que `pileTypes` não conhece é ignorada. Elas
   * não têm ambiente, parede ou elemento: nenhuma medida de AMBIENTE, PAREDE, PILAR ou VIGA as
   * enxerga (RN-360), e é por isso que entram numa conta à parte, em `pile`.
   */
  readonly pieces?: readonly StructuralPiece[];
  /** Os tipos de estaca da obra — o diâmetro, o comprimento padrão e o aço de cada estaca. */
  readonly pileTypes?: readonly PileType[];
  /** As estacas marcadas para cada serviço medido por `MARKED` (RN-365). */
  readonly pieceMarks?: readonly PieceMark[];
  /**
   * Os blocos desta tipologia (Fase 27, etapa 2 — D3). Peça própria, como a estaca: nenhuma
   * medida de AMBIENTE, PAREDE, PILAR, VIGA ou ESTACA os soma (RN-360), e é por isso que entram
   * numa conta à parte, em `pileCap`. Descontam o trecho que o baldrame tem dentro deles.
   */
  readonly pileCaps?: readonly PileCap[];
  /**
   * As vigas baldrame desta tipologia (D6). O comprimento livre de cada uma é o eixo menos o
   * trecho dentro dos blocos **da mesma tipologia** (RN-368); sem bloco na ponta, conta de eixo
   * a eixo (F14).
   */
  readonly groundBeams?: readonly GroundBeam[];
  /**
   * O radier e a laje desta tipologia (Fase 27, etapa 3 — D7). Peças próprias: nenhuma medida de
   * AMBIENTE, PAREDE, PILAR, VIGA, ESTACA, BLOCO ou BALDRAME as soma (RN-360), e é por isso que
   * entram numa conta à parte, em `raft` e `slab`.
   */
  readonly slabs?: readonly Slab[];
  /**
   * Os shafts **das tipologias do mesmo desenho**, já como buracos de laje (RN-370). O shaft é do
   * ambiente do apartamento e a laje é da área comum do andar, e por isso quem chama os traz das
   * outras tipologias. Só a laje os desconta; o radier não.
   */
  readonly slabOpenings?: readonly SlabOpening[];
  /**
   * A espessura da laje desenhada no andar, em metros (Fase 27, etapa 3 — D8, F13, RN-371).
   *
   * Presente, a viga conta só a parte **abaixo** da laje — a seção menos a espessura —, no volume
   * e nas duas faces laterais; o fundo não muda. Ausente, a viga é a de sempre: sem laje
   * desenhada, nada muda (F13). É a espessura da laje do andar, e não a da região sobre cada
   * viga: a viga sabe em que parede está, e não onde a parede está na planta.
   */
  readonly slabThicknessOverBeams?: number;
  /**
   * O muro de contenção desta tipologia (Fase 27, etapa 4 — D9, F2). Peça própria: nenhuma medida
   * de AMBIENTE, PAREDE, PILAR ou VIGA o soma (RN-360), e é por isso que entra numa conta à
   * parte, em `retainingWall`.
   */
  readonly retainingWalls?: readonly RetainingWall[];
  /**
   * O pilar de contenção desta tipologia (D9, F2, F6): também peça própria, fora da conta do
   * PILAR da estrutura. O encaixado no muro **da mesma conta** desconta e é descontado (RN-372).
   */
  readonly retainingColumns?: readonly RetainingColumn[];
  /**
   * A escada de concreto desta tipologia (Fase 27, etapa 5 — D10, F17). Peça própria: nenhuma
   * medida de AMBIENTE — nem a do ambiente ESCADA, que mede o acabamento — a soma (RN-360), e é
   * por isso que entra numa conta à parte, em `stair`.
   */
  readonly stairs?: readonly Stair[];
}

export function computeGeometry(input: ComputeGeometryInput): TakeoffGeometry {
  // O vizinho entra no mapa — para a parede achar o outro lado — e **não** na lista de
  // saída, que é o total desta tipologia.
  const roomById = new Map<RoomId, Room>(
    [...input.rooms, ...(input.neighbourRooms ?? [])].map((room) => [room.id, room]),
  );
  const commonAreas = new Set<UnitPositionId>(input.commonAreaPositionIds ?? []);
  const minhas =
    input.ownPositionIds === undefined ? undefined : new Set<UnitPositionId>(input.ownPositionIds);

  const openingsByWall = new Map<WallId, Opening[]>();
  for (const opening of input.openings ?? []) {
    const list = openingsByWall.get(opening.wallId);
    if (list === undefined) openingsByWall.set(opening.wallId, [opening]);
    else list.push(opening);
  }

  // A estrutura entra pela parede: cada trecho tira a sombra dele do plano em que está
  // (RN-189). O elemento fica de fora deste mapa de propósito — o volume é dele, e sai
  // por elemento mais abaixo (RN-190).
  const elementById = new Map<StructuralElementId, StructuralElement>(
    (input.elements ?? [])
      .filter((element) => element.deletedAt === undefined)
      .map((element) => [element.id, element]),
  );
  const wallById = new Map<WallId, Wall>(input.walls.map((wall) => [wall.id, wall]));
  const structureAreaByWall = new Map<WallId, number>();
  /** A estrutura de cada parede, para o revestimento saber o que há no plano dela. */
  const structureByWall = new Map<
    WallId,
    { element: StructuralElement; segment: ElementSegment }[]
  >();
  /** Quantos trechos cada elemento declarou, e quantos deles acharam parede no desenho. */
  const trechosOfElement = new Map<StructuralElementId, number>();
  const wallsOfElement = new Map<StructuralElementId, number>();
  /** Os trechos de cada elemento, para a corrida da viga sair da soma deles (fatia 4). */
  const segmentsOfElement = new Map<StructuralElementId, ElementSegment[]>();

  for (const segment of input.elementSegments ?? []) {
    const element = elementById.get(segment.elementId);
    if (element === undefined) continue;
    trechosOfElement.set(segment.elementId, (trechosOfElement.get(segment.elementId) ?? 0) + 1);

    const wall = wallById.get(segment.wallId);
    if (wall === undefined || wall.deletedAt !== undefined) continue;

    wallsOfElement.set(segment.elementId, (wallsOfElement.get(segment.elementId) ?? 0) + 1);
    const doElemento = segmentsOfElement.get(segment.elementId);
    if (doElemento === undefined) segmentsOfElement.set(segment.elementId, [segment]);
    else doElemento.push(segment);
    const naParede = structureByWall.get(segment.wallId);
    if (naParede === undefined) structureByWall.set(segment.wallId, [{ element, segment }]);
    else naParede.push({ element, segment });
    structureAreaByWall.set(
      segment.wallId,
      roundQuantity(
        (structureAreaByWall.get(segment.wallId) ?? 0) + segmentShadowArea(element, segment, wall),
        GEOMETRY_DECIMALS,
      ),
    );
  }

  const orphanElementIds: StructuralElementId[] = [];
  const elements: ElementGeometry[] = [];
  let volume = 0;
  let espalaLength = 0;
  const volumeByKind: Record<StructuralElementKind, number> = { PILAR: 0, VIGA: 0 };
  const espalaByKind: Record<StructuralElementKind, number> = { PILAR: 0, VIGA: 0 };
  // As quatro da fatia 10: faces, fundo, corrida e contagem — por peça, como as duas de cima.
  const facesByKind: Record<StructuralElementKind, number> = { PILAR: 0, VIGA: 0 };
  const bottomByKind: Record<StructuralElementKind, number> = { PILAR: 0, VIGA: 0 };
  const runByKind: Record<StructuralElementKind, number> = { PILAR: 0, VIGA: 0 };
  const countByKind: Record<StructuralElementKind, number> = { PILAR: 0, VIGA: 0 };
  for (const element of elementById.values()) {
    // Elemento que só encosta em parede fora do desenho é o mesmo caso da parede órfã:
    // fica registrado e de fora da conta, em vez de derrubá-la.
    if (
      (trechosOfElement.get(element.id) ?? 0) > 0 &&
      (wallsOfElement.get(element.id) ?? 0) === 0
    ) {
      orphanElementIds.push(element.id);
      continue;
    }

    // A corrida da viga é a soma dos trechos, e não um número ao lado deles (fatia 4).
    const trechos = segmentsOfElement.get(element.id) ?? [];
    const run = elementRun(element, trechos, wallById);
    // Onde há laje desenhada, a viga conta só a parte abaixo dela — no volume e nas faces
    // laterais; o fundo e o pilar não mudam (RN-371). Sem espessura, é a seção inteira (F13).
    const sobLaje =
      element.kind === 'VIGA'
        ? {
            ...element,
            sectionDepth: beamDepthBelowSlab(element.sectionDepth, input.slabThicknessOverBeams),
          }
        : element;
    const doElemento = elementVolume(sobLaje, run);
    const shares = splitVolume(doElemento, trechos, wallById, commonAreas);
    const espala = elementEspala(element, trechos, wallById, run);
    const espalaShares = splitEspala(element, trechos, wallById, run);
    const faceArea = elementFaces(sobLaje, run);
    const bottomArea = elementBottom(element, run);

    // A parte que é desta tipologia, como na alvenaria: a viga que corre em cima da
    // divisa é metade de cada apto, e inteira do apto quando o vizinho é área comum.
    const minhaParte =
      minhas === undefined
        ? doElemento
        : shares
            .filter((share) => share.positionId === undefined || minhas.has(share.positionId))
            .reduce((total, share) => total + share.volume, 0);

    volume = roundQuantity(volume + minhaParte, VOLUME_DECIMALS);
    volumeByKind[element.kind] = roundQuantity(
      volumeByKind[element.kind] + minhaParte,
      VOLUME_DECIMALS,
    );

    // A espala não rateia: cada lado leva as faces que vê (RN-178). O filtro é o mesmo,
    // mas o que ele separa já vem separado por dono, e não por fração.
    const minhaEspala =
      minhas === undefined
        ? espala
        : espalaShares
            .filter((share) => share.positionId === undefined || minhas.has(share.positionId))
            .reduce((total, share) => total + share.length, 0);

    espalaLength = roundQuantity(espalaLength + minhaEspala, GEOMETRY_DECIMALS);
    espalaByKind[element.kind] = roundQuantity(
      espalaByKind[element.kind] + minhaEspala,
      GEOMETRY_DECIMALS,
    );

    // Faces, fundo, corrida e contagem são da **peça**, como o concreto: a que corre em
    // cima da divisa é meia de cada apto, e a peça dentro do apto é inteira dele.
    const minhaFracao = minhaFracaoDoElemento(
      elementFractions(trechos, wallById, commonAreas),
      minhas,
    );
    const somar = (total: Record<StructuralElementKind, number>, valor: number): void => {
      total[element.kind] = roundQuantity(
        total[element.kind] + valor * minhaFracao,
        GEOMETRY_DECIMALS,
      );
    };
    somar(facesByKind, faceArea);
    somar(bottomByKind, bottomArea);
    somar(runByKind, run);
    somar(countByKind, 1);

    elements.push({
      elementId: element.id,
      run,
      volume: doElemento,
      shares,
      espala,
      espalaShares,
      faceArea,
      bottomArea,
      ownMeasures: {
        VOLUME: minhaParte,
        ESPALA: minhaEspala,
        FACE: faceArea * minhaFracao,
        BOTTOM: bottomArea * minhaFracao,
        LENGTH: run * minhaFracao,
        COUNT: minhaFracao,
      },
    });
  }

  const faceAreaByRoom = new Map<RoomId, number>();
  /** Quanto do perímetro de cada ambiente **não** leva rodapé. Ver `skirtingLength`. */
  const skirtingGapByRoom = new Map<RoomId, number>();
  const semRodape = (roomId: RoomId, quanto: number): void => {
    skirtingGapByRoom.set(
      roomId,
      roundQuantity((skirtingGapByRoom.get(roomId) ?? 0) + quanto, GEOMETRY_DECIMALS),
    );
  };
  const walls: WallGeometry[] = [];
  const orphanWallIds: WallId[] = [];
  let wallArea = 0;
  let outsideFaceArea = 0;
  // As três da parede e os quatro números de cada vão (fatia 10).
  let wallLength = 0;
  let wallVolumeTotal = 0;
  let wallCount = 0;
  const openingsByKind = emptyOpenings();
  /** O guarda-corpo, em metro, metro quadrado e peça — ver `RailingMeasure`. */
  let railing: RailingMeasure = { length: 0, area: 0, count: 0 };
  const somarGuardaCorpo = (comprimento: number, altura: number, parte: number): void => {
    railing = {
      length: roundQuantity(railing.length + comprimento * parte, GEOMETRY_DECIMALS),
      area: roundQuantity(railing.area + comprimento * altura * parte, GEOMETRY_DECIMALS),
      count: roundQuantity(railing.count + parte, GEOMETRY_DECIMALS),
    };
  };

  for (const wall of input.walls) {
    if (wall.deletedAt !== undefined) continue;

    const sides = wall.sideB === undefined ? [wall.sideA] : [wall.sideA, wall.sideB];
    if (sides.some((side) => !roomById.has(side.roomId))) {
      orphanWallIds.push(wall.id);
      continue;
    }

    const openings = openingsByWall.get(wall.id) ?? [];
    const grossArea = wallGrossArea(wall);
    const openingArea = openingsArea(openings);
    const structureArea = structureAreaByWall.get(wall.id) ?? 0;
    const netArea = wallNetArea(wall, openings, structureArea);

    const shares = splitShares(wall, netArea, commonAreas);

    // A parte que é desta tipologia. Divisa entra pela metade — ou nem entra, quando o
    // outro lado é o apto e este é a área comum (RN-163). A parede sem posição nenhuma é
    // interna: inteira de quem a contém.
    const minhaParte =
      minhas === undefined
        ? netArea
        : shares
            .filter((share) => share.positionId === undefined || minhas.has(share.positionId))
            .reduce((total, share) => total + share.area, 0);

    wallArea = roundQuantity(wallArea + minhaParte, GEOMETRY_DECIMALS);
    const comprimento = wall.joined ? 0 : wall.length;
    const volumeDaParede = wallVolume(wall, netArea);
    walls.push({
      wallId: wall.id,
      grossArea,
      openingArea,
      structureArea,
      netArea,
      length: comprimento,
      volume: volumeDaParede,
      shares,
    });

    // Comprimento, volume, contagem e vãos seguem a **alvenaria**: onde a parede não existe
    // (conjugada) não há o que medir, e a divisa vai em fração — meia para cada apto, inteira
    // para o apto quando o vizinho é área comum (RN-163). Um vão numa parede que não existe
    // também não existe: a porta da parede conjugada é a abertura inteira, e não mais uma porta.
    if (!wall.joined) {
      const minhaFracao = minhaFracaoDoElemento(positionShares(wall, commonAreas), minhas);
      wallLength = roundQuantity(wallLength + comprimento * minhaFracao, GEOMETRY_DECIMALS);
      wallVolumeTotal = roundQuantity(
        wallVolumeTotal + volumeDaParede * minhaFracao,
        VOLUME_DECIMALS,
      );
      wallCount = roundQuantity(wallCount + minhaFracao, GEOMETRY_DECIMALS);
      for (const opening of openings) {
        const total = openingsByKind[opening.kind];
        total.area = roundQuantity(
          total.area + areaOfOpening(opening) * minhaFracao,
          GEOMETRY_DECIMALS,
        );
        total.count = roundQuantity(total.count + opening.count * minhaFracao, GEOMETRY_DECIMALS);
        total.contour = roundQuantity(
          total.contour + contourOfOpening(opening) * minhaFracao,
          GEOMETRY_DECIMALS,
        );
        total.width = roundQuantity(
          total.width + widthOfOpening(opening) * minhaFracao,
          GEOMETRY_DECIMALS,
        );
      }
    }

    // O guarda-corpo em cima da mureta entra nas medidas do guarda-corpo, além de a mureta
    // entrar como parede (RN-268) — e segue o rateio da parede que o carrega (RN-163).
    // Conjugada não tem mureta, e sem mureta não há onde prendê-lo.
    if (wall.railingHeight !== undefined && !wall.joined) {
      const parte = minhaFracaoDoElemento(positionShares(wall, commonAreas), minhas);
      somarGuardaCorpo(wall.length, wall.railingHeight, parte);
    }

    // Onde não há parede não há face nem rodapé: conjugado não recebe reboco de parede
    // nenhuma, e o rodapé não atravessa a abertura que a conjugação deixa — ela é o
    // trecho inteiro, e não um vão dentro dele.
    if (wall.joined) {
      for (const side of sides) semRodape(side.roomId, wall.length);
      continue;
    }

    const naPorta = floorOpeningsWidth(openings);
    const estrutura = structureByWall.get(wall.id) ?? [];

    /**
     * O revestimento de um lado, com a estrutura que ele vê (RN-185, RN-186).
     *
     * **Desconta e devolve**: o plano perde a sombra do elemento e recebe de volta a
     * superfície que ele oferece àquele lado. Rente, os dois são o mesmo número e a face
     * não muda — que é o certo, porque a elevação continua sendo rebocada, sobre concreto
     * em vez de sobre bloco. Sobrando para dentro, a devolução vem maior: são as faixas
     * que o avanço criou, e elas vão **inteiras** para cada lado em que ele aparece, sem
     * repartir.
     */
    const faceComEstrutura = (lado: 'A' | 'B', altura: number): number => {
      const comEstrutura = estrutura.reduce(
        (total, { element, segment }) => {
          const { discount, given } = segmentFaceArea(element, segment, wall, lado, altura);
          return total - discount + given;
        },
        faceArea(wall.length, altura, openingArea),
      );
      return comEstrutura > 0 ? roundQuantity(comEstrutura, GEOMETRY_DECIMALS) : 0;
    };

    for (const [indice, side] of sides.entries()) {
      const room = roomById.get(side.roomId)!;
      // A face sobe até o forro — ou até onde a parede vai, quando ela para antes: a mureta
      // de 1,10 da varanda não tem reboco até o pé-direito de 2,60 (D25).
      const face = faceComEstrutura(
        indice === 0 ? 'A' : 'B',
        Math.min(room.ceilingHeight, wall.height),
      );
      faceAreaByRoom.set(
        side.roomId,
        roundQuantity((faceAreaByRoom.get(side.roomId) ?? 0) + face, GEOMETRY_DECIMALS),
      );
      semRodape(side.roomId, naPorta);
    }

    if (isExternal(wall)) {
      // A face de fora não encara ambiente nenhum — é fachada, e tem a altura da parede.
      // Do lado da rua também sobra pilar: o que avança para lá é revestimento de fachada.
      outsideFaceArea = roundQuantity(
        outsideFaceArea + faceComEstrutura('B', wall.height),
        GEOMETRY_DECIMALS,
      );
    }
  }

  // As paredes de cada ambiente, para o contorno sair delas e não do retângulo.
  const wallsByRoom = new Map<RoomId, number[]>();
  for (const wall of input.walls) {
    if (wall.deletedAt !== undefined) continue;
    for (const side of wall.sideB === undefined ? [wall.sideA] : [wall.sideA, wall.sideB]) {
      if (!roomById.has(side.roomId)) continue;
      const lista = wallsByRoom.get(side.roomId);
      if (lista === undefined) wallsByRoom.set(side.roomId, [wall.length]);
      else lista.push(wall.length);
    }
  }

  // O guarda-corpo do lado sem parede é inteiro do ambiente: o lado é do contorno dele. O
  // vizinho de fora (`neighbourRooms`) não entra — o lado dele é da tipologia dele.
  for (const room of input.rooms) {
    if (room.deletedAt !== undefined) continue;
    for (const { edge, length } of edgesWithoutWallOf(room)) {
      if (edge.kind === 'GUARDA_CORPO') somarGuardaCorpo(length, edge.height, 1);
    }
  }

  // O shaft é do ambiente, e só entra o dos ambientes desta conta: o do vizinho, que veio
  // só para dar lado à divisa, é da tipologia dele (RN-272).
  const destaConta = new Set(
    input.rooms.filter((room) => room.deletedAt === undefined).map((room) => room.id),
  );
  const shaftAreaByRoom = new Map<RoomId, number>();
  let shaft: ShaftTotals = NO_SHAFTS;
  for (const buraco of input.shafts ?? []) {
    if (buraco.deletedAt !== undefined || !destaConta.has(buraco.roomId)) continue;
    const area = shaftArea(buraco);
    shaftAreaByRoom.set(
      buraco.roomId,
      roundQuantity((shaftAreaByRoom.get(buraco.roomId) ?? 0) + area, GEOMETRY_DECIMALS),
    );
    shaft = {
      area: roundQuantity(shaft.area + area, GEOMETRY_DECIMALS),
      contour: roundQuantity(shaft.contour + shaftContour(buraco), GEOMETRY_DECIMALS),
      count: shaft.count + buraco.count,
    };
  }

  const rooms = input.rooms
    .filter((room) => room.deletedAt === undefined)
    .map((room): RoomGeometry => {
      const comprimentos = wallsByRoom.get(room.id) ?? [];
      const contorno =
        room.outline !== undefined && isValidOutline(room.outline) ? room.outline : undefined;
      const doContorno = contorno === undefined ? undefined : polygonArea(contorno);
      const dasParedes = areaFromWalls(comprimentos);

      // A ordem é a da confiança: o que alguém decidiu, o que a planta mostra, o que as
      // paredes determinam, e por último o retângulo do cadastro.
      const area =
        room.areaOverride ??
        doContorno ??
        dasParedes ??
        roundQuantity(room.width * room.length, GEOMETRY_DECIMALS);
      const areaSource: AreaSource =
        room.areaOverride !== undefined
          ? 'CORRIGIDA'
          : doContorno !== undefined
            ? 'CONTORNO'
            : dasParedes !== undefined
              ? 'PAREDES'
              : 'MEDIDAS';

      // O lado sem parede — guarda-corpo ou aberto — fica fora do perímetro, e com isso do
      // rodapé, que desconta dele (RN-267). O piso e o teto seguem inteiros: o contorno
      // continua fechando o ambiente. A correção à mão continua vencendo.
      const perimeter =
        room.perimeterOverride ??
        (contorno !== undefined
          ? perimetroComParede(room, contorno)
          : comprimentos.length > 0
            ? roundQuantity(
                comprimentos.reduce((total, comprimento) => total + comprimento, 0),
                GEOMETRY_DECIMALS,
              )
            : roomPerimeter(room));

      // O rodapé desconta do perímetro **que vale**, seja ele o do contorno, o das
      // paredes ou o corrigido à mão: descontar de outro número faria a correção de
      // alguém valer para a soleira e não para o rodapé. Nunca negativo — porta maior que
      // a parede é erro de digitação, e o lugar de recusá-lo é a validação.
      const semRodapeAqui = skirtingGapByRoom.get(room.id) ?? 0;
      const skirtingLength = Math.max(
        0,
        roundQuantity(perimeter - semRodapeAqui, GEOMETRY_DECIMALS),
      );

      // O shaft desconta da área **que vale** — a do contorno, a das paredes ou a corrigida
      // à mão —, pelo mesmo motivo do rodapé: descontar de outro número faria a correção de
      // alguém valer para um e não para o outro. Nunca negativo: shaft maior que o ambiente
      // é traço errado, e o piso não vira laje a devolver (RN-271).
      const noShaft = shaftAreaByRoom.get(room.id) ?? 0;
      const semShaft = Math.max(0, roundQuantity(area - noShaft, GEOMETRY_DECIMALS));

      return {
        roomId: room.id,
        floorArea: semShaft,
        ceilingArea: semShaft,
        ...(noShaft > 0 ? { shaftArea: noShaft } : {}),
        perimeter,
        skirtingLength,
        faceArea: faceAreaByRoom.get(room.id) ?? 0,
        areaSource,
        wallCount: comprimentos.length,
        // Do piso que **vale** — o do contorno, o das paredes ou o corrigido —, e não de
        // outro: o volume de quem corrigiu a área tem de acompanhar a correção. Já sem o
        // shaft, como o piso: o buraco da prumada não é ar do ambiente (RN-271).
        volume: roundQuantity(semShaft * room.ceilingHeight, VOLUME_DECIMALS),
      };
    });

  return {
    rooms,
    walls,
    wallArea,
    outsideFaceArea,
    volume,
    volumeByKind,
    espalaLength,
    espalaByKind,
    elements,
    railing,
    shaft,
    // A estaca não passa pelo ambiente nem pela parede: é conta própria (RN-360).
    pile: pileTotals(input.pieces ?? [], input.pileTypes ?? [], input.pieceMarks ?? []),
    // O bloco e o baldrame também não passam pelo ambiente nem pela parede (RN-360); o
    // baldrame desconta os blocos da mesma conta (RN-368).
    pileCap: pileCapTotals(input.pileCaps ?? []),
    groundBeam: groundBeamTotals(input.groundBeams ?? [], input.pileCaps ?? []),
    // O radier e a laje também ficam fora do ambiente e da parede (RN-360); só a laje perde o
    // que os shafts ocupam (RN-370).
    raft: slabTotals(input.slabs ?? [], 'RADIER'),
    slab: slabTotals(input.slabs ?? [], 'LAJE', input.slabOpenings ?? []),
    // O muro e o pilar de contenção são peças próprias (F2): nem a alvenaria mede o muro, nem a
    // concretagem de PILARES mede o pilar. Um desconta o outro, na mesma tipologia (RN-372).
    retainingWall: retainingWallTotals(input.retainingWalls ?? [], input.retainingColumns ?? []),
    retainingColumn: retainingColumnTotals(
      input.retainingColumns ?? [],
      input.retainingWalls ?? [],
    ),
    // A escada é peça própria (F17): o ambiente ESCADA mede o acabamento, e ela, o concreto.
    stair: stairTotals(input.stairs ?? []),
    orphanWallIds,
    orphanElementIds,
    wallLength,
    wallVolume: wallVolumeTotal,
    wallCount,
    facesByKind,
    bottomByKind,
    runByKind,
    countByKind,
    openingsByKind,
  };
}

/** Os vãos zerados — um registro por tipo, e mutável: é onde a conta acumula. */
function emptyOpenings(): Record<OpeningKind, { -readonly [K in keyof OpeningTotals]: number }> {
  const zero = () => ({ area: 0, count: 0, contour: 0, width: 0 });
  return { PORTA: zero(), JANELA: zero(), PASSAGEM: zero() };
}

/**
 * De quem é o elemento, em frações — o rateio do concreto (`splitVolume`) sem o concreto.
 *
 * Faces, fundo, corrida e contagem se repartem como o volume: a peça é uma só, e o que a
 * divide é a parede em que ela está (RN-163). O peso de cada trecho é o mesmo de
 * `splitVolume`, que lê daqui — duas cópias da regra divergiriam no dia em que alguém mudasse
 * uma.
 */
function elementFractions(
  segments: readonly ElementSegment[],
  walls: ReadonlyMap<WallId, Wall>,
  commonAreas: ReadonlySet<UnitPositionId>,
): readonly PositionShare[] {
  const peso = new Map<UnitPositionId | undefined, number>();
  let trechos = 0;
  for (const segment of segments) {
    const wall = walls.get(segment.wallId);
    if (wall === undefined || wall.deletedAt !== undefined) continue;
    trechos += 1;
    for (const parte of positionShares(wall, commonAreas)) {
      peso.set(parte.positionId, (peso.get(parte.positionId) ?? 0) + parte.fraction);
    }
  }

  // Elemento sem trecho em parede nenhuma — o vão livre solto — não pertence a posição
  // alguma: entra inteiro no desenho, como a parede interna de uma tipologia sozinha.
  if (trechos === 0) return [{ fraction: 1 }];

  return [...peso.entries()].map(([positionId, acumulado]) => ({
    fraction: acumulado / trechos,
    ...(positionId === undefined ? {} : { positionId }),
  }));
}

/** Quanto do rateio é **desta** tipologia: tudo, ou a parte das posições que contam como minhas. */
function minhaFracaoDoElemento(
  partes: readonly PositionShare[],
  minhas: ReadonlySet<UnitPositionId> | undefined,
): number {
  if (minhas === undefined) return 1;
  return partes
    .filter((parte) => parte.positionId === undefined || minhas.has(parte.positionId))
    .reduce((total, parte) => total + parte.fraction, 0);
}

/** Um lado sem parede no lugar dele: o trecho do contorno e quanto ele mede. */
export interface PlacedEdgeWithoutWall {
  readonly edge: EdgeWithoutWall;
  readonly segment: Segment;
  /** Metros, nas duas casas da geometria. */
  readonly length: number;
}

/**
 * Os lados sem parede que o contorno de **hoje** tem (D25).
 *
 * O índice que aponta além do contorno — o ambiente redesenhado com menos cantos — fica de
 * fora, como a parede que perdeu o lado: não mede nada até alguém dizer o tipo de novo. Sem
 * contorno válido não há lado, e o ambiente digitado continua todo parede.
 */
export function edgesWithoutWallOf(
  room: Pick<Room, 'outline' | 'edgesWithoutWall'>,
): readonly PlacedEdgeWithoutWall[] {
  const { outline, edgesWithoutWall } = room;
  if (edgesWithoutWall === undefined || outline === undefined || !isValidOutline(outline)) {
    return [];
  }
  const lados = polygonEdges(outline);
  return edgesWithoutWall.flatMap((edge) => {
    const segment = lados[edge.edgeIndex];
    return segment === undefined
      ? []
      : [{ edge, segment, length: roundQuantity(segmentLength(segment), GEOMETRY_DECIMALS) }];
  });
}

/**
 * O perímetro do contorno que é parede — os lados de guarda-corpo e abertos de fora (RN-267).
 *
 * Soma os lados que ficam, em vez de descontar os que saem do perímetro já arredondado: é a
 * mesma conta de `polygonPerimeter`, e o ambiente sem lado nenhum sem parede dá o mesmo
 * número de antes, ao centímetro.
 */
function perimetroComParede(
  room: Pick<Room, 'outline' | 'edgesWithoutWall'>,
  contorno: Parameters<typeof polygonPerimeter>[0],
): number {
  const semParede = new Set(edgesWithoutWallOf(room).map(({ edge }) => edge.edgeIndex));
  if (semParede.size === 0) return polygonPerimeter(contorno);
  const total = polygonEdges(contorno).reduce(
    (soma, lado, indice) => (semParede.has(indice) ? soma : soma + segmentLength(lado)),
    0,
  );
  return roundQuantity(total, GEOMETRY_DECIMALS);
}

/**
 * A área que as paredes permitem calcular — e só ela.
 *
 * Quatro paredes que se emparelham duas a duas fecham um retângulo, e aí a área é o
 * produto dos dois lados. **Qualquer outra coisa não dá**: comprimento de aresta não
 * determina área. Três paredes de 3, 4 e 5 metros formam um triângulo; quatro de 3, 4, 3 e
 * 4 formam tanto um retângulo de 12 m² quanto um losango de menos que isso; cinco paredes
 * de um ambiente em L admitem infinitas áreas.
 *
 * Devolver `undefined` nesses casos é o ponto: o sistema diz que não sabe, a conta cai em
 * largura × comprimento e quem conhece a planta corrige. Inventar um número aqui seria
 * pior do que não ter — ele entraria no orçamento com cara de calculado.
 */
function areaFromWalls(comprimentos: readonly number[]): number | undefined {
  if (comprimentos.length !== 4) return undefined;

  const ordenados = [...comprimentos].sort((a, b) => a - b);
  const [a, b, c, d] = ordenados as [number, number, number, number];
  const pareia = (x: number, y: number): boolean => Math.abs(x - y) < 0.005;
  if (!pareia(a, b) || !pareia(c, d)) return undefined;

  return roundQuantity(((a + b) / 2) * ((c + d) / 2), GEOMETRY_DECIMALS);
}

/** Face de parede vista de dentro do ambiente: comprimento × pé-direito, menos os vãos. */
function faceArea(length: number, ceilingHeight: number, openingArea: number): number {
  const gross = length * ceilingHeight;
  const net = gross - openingArea;
  return net > 0 ? roundQuantity(net, GEOMETRY_DECIMALS) : 0;
}

/**
 * O rateio (RN-163).
 *
 * Divisa entre dois aptos vai meio a meio. Parede entre apto e área comum é **inteira do
 * apto** — e é por isso que a conta precisa saber quem é área comum: as duas são "lados em
 * posições diferentes" e só o tipo da posição as separa. Qualquer outra parede é inteira
 * de quem a contém.
 *
 * A segunda metade sai por **diferença**, e não de um segundo arredondamento: 7,25 m²
 * partido ao meio daria 3,63 + 3,63 = 7,26, e o prédio ganharia um centímetro quadrado por
 * parede — pouco em uma, visível em duas mil.
 */
function splitShares(
  wall: Wall,
  netArea: number,
  commonAreas: ReadonlySet<UnitPositionId>,
): readonly WallShare[] {
  if (netArea === 0) return [];

  const partes = positionShares(wall, commonAreas);
  if (partes.length === 1) {
    const [unica] = partes as [PositionShare];
    return [
      {
        fraction: 1,
        area: netArea,
        ...(unica.positionId === undefined ? {} : { positionId: unica.positionId }),
      },
    ];
  }

  const [a, b] = partes as [PositionShare, PositionShare];
  const first = roundQuantity(netArea / 2, GEOMETRY_DECIMALS);
  const second = roundQuantity(netArea - first, GEOMETRY_DECIMALS);
  return [
    { positionId: a.positionId!, fraction: 0.5, area: first },
    { positionId: b.positionId!, fraction: 0.5, area: second },
  ];
}

/** De quem é esta parede, em frações — o rateio da RN-163 sem nenhuma grandeza junto. */
interface PositionShare {
  readonly positionId?: UnitPositionId;
  readonly fraction: number;
}

/**
 * A quem a parede pertence, e em que proporção (RN-163).
 *
 * Existe separado de `splitShares` porque o rateio não é só de área: o concreto da viga que
 * corre em cima da divisa se reparte pela mesma regra, e duas cópias dela divergiriam no
 * dia em que alguém mudasse uma.
 */
function positionShares(
  wall: Pick<Wall, 'sideA' | 'sideB'>,
  commonAreas: ReadonlySet<UnitPositionId>,
): readonly PositionShare[] {
  const whole = (positionId: UnitPositionId | undefined): readonly PositionShare[] => [
    { fraction: 1, ...(positionId === undefined ? {} : { positionId }) },
  ];

  if (!isPartyWall(wall)) return whole(wall.sideA.positionId);

  const a = wall.sideA.positionId!;
  const b = wall.sideB!.positionId!;
  const aIsCommon = commonAreas.has(a);
  const bIsCommon = commonAreas.has(b);
  if (aIsCommon !== bIsCommon) return whole(aIsCommon ? b : a);

  return [
    { positionId: a, fraction: 0.5 },
    { positionId: b, fraction: 0.5 },
  ];
}

/**
 * De quem é o concreto do elemento (RN-163 aplicada ao pilar e à viga).
 *
 * O volume é do **objeto** (RN-190), e o objeto pode estar em mais de uma parede. Cada
 * trecho puxa o elemento para o rateio da parede em que está: viga em cima da divisa é
 * meio a meio, viga dentro do apto é inteira dele, e viga entre o apto e o hall é inteira
 * do apto — a mesma regra que a alvenaria já usa, e não uma segunda inventada aqui.
 *
 * **Cada trecho pesa igual.** Ponderar pela corrida seria mais fino, mas a viga que
 * atravessa uma parede sem espessura cadastrada consome zero dela (fatia 4) — e aquele
 * apto perderia o concreto inteiro por causa de um campo em branco.
 *
 * A última parte sai por **diferença**, como a segunda metade da parede: dois
 * arredondamentos de 0,1155 dariam 0,232 onde o elemento tem 0,231.
 */
function splitVolume(
  total: number,
  segments: readonly ElementSegment[],
  walls: ReadonlyMap<WallId, Wall>,
  commonAreas: ReadonlySet<UnitPositionId>,
): readonly VolumeShare[] {
  if (total === 0) return [];

  const partes = elementFractions(segments, walls, commonAreas);
  let distribuido = 0;
  return partes.map(({ positionId, fraction }, indice) => {
    const ultima = indice === partes.length - 1;
    const volume = ultima
      ? roundQuantity(total - distribuido, VOLUME_DECIMALS)
      : roundQuantity(total * fraction, VOLUME_DECIMALS);
    distribuido = roundQuantity(distribuido + volume, VOLUME_DECIMALS);
    return { fraction, volume, ...(positionId === undefined ? {} : { positionId }) };
  });
}

/**
 * De quem é a espala do elemento (RN-178 aplicada à quina do pilar e da viga).
 *
 * **Não é rateio, é dono.** O concreto da viga em cima da divisa é meio a meio porque a
 * peça é uma só e os dois aptos a compram juntos. A espala é o arremate de uma face, e a
 * face do avanço para dentro do apto 01 só existe no apto 01 — o 02 não vê quina nenhuma e
 * não paga por ela. Ratear aqui daria a cada um metade de um arremate que um dos dois vai
 * fazer inteiro.
 *
 * A corrida **não** se acumula (RN-190): o que soma por trecho é a contagem de faces, e a
 * corrida do objeto entra uma vez, multiplicando. Faces é inteiro e a corrida já vem
 * arredondada, então a soma das partes fecha com o total sem precisar da última por
 * diferença, que é o que `splitVolume` faz com a fração.
 */
function splitEspala(
  element: Pick<StructuralElement, 'kind' | 'sectionWidth' | 'sectionDepth'>,
  segments: readonly ElementSegment[],
  walls: ReadonlyMap<WallId, Wall>,
  run: number,
): readonly EspalaShare[] {
  const porPosicao = new Map<UnitPositionId | undefined, number>();
  for (const segment of segments) {
    const wall = walls.get(segment.wallId);
    if (wall === undefined || wall.deletedAt !== undefined) continue;
    // Conjugada não tem parede, e sem parede não há avanço a arrematar.
    if (wall.joined) continue;

    for (const lado of ['A', 'B'] as const) {
      const faces = espalaFaces(element, segment, wall, lado);
      if (faces === 0) continue;
      // O lado B da parede externa é a rua: a quina existe e é da fachada, sem apto dono.
      const side = lado === 'A' ? wall.sideA : wall.sideB;
      porPosicao.set(side?.positionId, (porPosicao.get(side?.positionId) ?? 0) + faces);
    }
  }

  return [...porPosicao.entries()].map(([positionId, faces]) => ({
    faces,
    length: roundQuantity(faces * run, GEOMETRY_DECIMALS),
    ...(positionId === undefined ? {} : { positionId }),
  }));
}

/**
 * O concreto de cada apto do pavimento tipo — o par de `wallAreaByPosition`.
 *
 * Elementos sem posição — os de uma tipologia montada sozinha — ficam de fora do mapa,
 * porque ainda não pertencem a apto nenhum.
 */
export function volumeByPosition(
  geometry: Pick<TakeoffGeometry, 'elements'>,
): ReadonlyMap<UnitPositionId, number> {
  const byPosition = new Map<UnitPositionId, number>();
  for (const element of geometry.elements) {
    for (const share of element.shares) {
      if (share.positionId === undefined) continue;
      byPosition.set(
        share.positionId,
        roundQuantity((byPosition.get(share.positionId) ?? 0) + share.volume, VOLUME_DECIMALS),
      );
    }
  }
  return byPosition;
}

/**
 * A alvenaria de cada apto do pavimento tipo.
 *
 * Paredes sem posição — as internas de uma tipologia sendo montada sozinha — ficam de
 * fora do mapa, porque ainda não pertencem a apto nenhum.
 */
export function wallAreaByPosition(
  geometry: Pick<TakeoffGeometry, 'walls'>,
): ReadonlyMap<UnitPositionId, number> {
  const byPosition = new Map<UnitPositionId, number>();
  for (const wall of geometry.walls) {
    for (const share of wall.shares) {
      if (share.positionId === undefined) continue;
      byPosition.set(
        share.positionId,
        roundQuantity((byPosition.get(share.positionId) ?? 0) + share.area, GEOMETRY_DECIMALS),
      );
    }
  }
  return byPosition;
}

/** A área que o serviço consome, conforme o jeito dele de medir. */
export function measuredArea(
  measureBy: MeasureBy,
  room: RoomGeometry,
  wallAreaOfRoom: number,
): number | undefined {
  switch (measureBy) {
    case 'FLOOR':
      return room.floorArea;
    case 'CEILING':
      return room.ceilingArea;
    case 'PERIMETER':
      return room.perimeter;
    case 'SKIRTING':
      return room.skirtingLength;
    case 'FACE':
      return room.faceArea;
    case 'WALL':
      return wallAreaOfRoom;
    // Volume e espala são do elemento, e o elemento atravessa ambientes: a viga que cruza
    // três paredes não é de nenhum dos três quartos. Os dois respondem no total da
    // tipologia, em `totalFor`, e não aqui.
    case 'VOLUME':
    case 'ESPALA':
      return undefined;
    // Serviço por unidade não sai da geometria: quantas tomadas tem o quarto é decisão de
    // projeto, não consequência das medidas.
    case 'UNIT':
      return undefined;
    // Nem da geometria, nem de decisão: o modelo já trouxe o número. O traçado não
    // confirma nem contradiz — ele não mediu esta peça.
    case 'MODEL':
      return undefined;
    // Contagem, comprimento, fundo, contorno e largura são da peça ou do vão, e ficam por
    // conta de `totalFor`: o ambiente não os conhece (fatia 10). Como o volume, respondem no
    // total da tipologia, e não por ambiente. Do shaft, o ambiente já recebe o que interessa a
    // ele, que é o piso e o teto a menos (RN-271).
    case 'COUNT':
    case 'LENGTH':
    case 'AREA':
    case 'BOTTOM':
    case 'CONTOUR':
    case 'WIDTH':
      return undefined;
    // O aço é da peça, como o volume de que ele sai: responde no total, em `totalFor`.
    case 'ACO':
      return undefined;
    // As marcadas são da estaca, e a estaca não é de ambiente nenhum (RN-360).
    case 'MARKED':
      return undefined;
    // O fundo + borda é da laje, e a laje também não é de ambiente nenhum (RN-360).
    case 'AREA_CONTOUR':
      return undefined;
  }
}

export interface GeometryTotals {
  /** Alvenaria — a parede contada uma vez. */
  readonly wallArea: number;
  /** Revestimento de parede — as duas faces de cada parede interna. */
  readonly faceArea: number;
  readonly floorArea: number;
  readonly ceilingArea: number;
  /**
   * A soma dos perímetros cheios dos ambientes. Era a medida da soleira e do peitoril; desde a
   * RN-309 os dois medem a largura do vão, com a margem de cada lado.
   */
  readonly perimeter: number;
  /** Rodapé — o mesmo perímetro, menos as portas e o que a conjugação abriu. */
  readonly skirtingLength: number;
  readonly outsideFaceArea: number;
  /**
   * Concreto em m³, **por peça** (RN-188, RN-257).
   *
   * É o oitavo número e o primeiro cúbico: os outros sete são m² ou metro linear. Ele vem
   * com `MeasureBy: 'VOLUME'` atrás — número sem medida apareceria na tela sem que serviço
   * nenhum conseguisse puxá-lo, que é o mesmo que não existir (ADR-116).
   *
   * Separado por peça desde a Fase 24, e **sem** a soma ao lado: a soma era o que a
   * CONCRETAGEM DE PILAR recebia, com o concreto das vigas dentro. Sem ela aqui, serviço
   * nenhum consegue voltar a misturar as duas (ADR-147).
   */
  readonly volumeByKind: Readonly<Record<StructuralElementKind, number>>;
  /**
   * Espala em metro linear, por peça (RN-187, RN-257).
   *
   * O nono, e o terceiro linear — ao lado do perímetro e do rodapé, e pela mesma razão de
   * eles serem dois: são medidas lineares **diferentes**, e um número só daria a uma a
   * medida da outra. Rodapé corre no encontro do piso com a parede; espala sobe na quina
   * do concreto que avança para dentro do ambiente.
   */
  readonly espalaByKind: Readonly<Record<StructuralElementKind, number>>;
  // As medidas da matriz completa (Fase 24, fatia 10, ADR-148) — cada uma com a unidade que a
  // grandeza leva em `MEASURE_UNIT`. Todas por peça, como as duas de cima: pilar e viga nunca
  // se somam (RN-257), e o vão de um tipo nunca é o de outro.

  /** Comprimento das paredes que existem (m). */
  readonly wallLength: number;
  /** Área líquida × espessura das paredes (m³). Parede sem espessura não entra. */
  readonly wallVolume: number;
  /** Quantas paredes (un). */
  readonly wallCount: number;
  /** Quantos ambientes (un). */
  readonly roomCount: number;
  /** Piso × pé-direito de cada ambiente (m³). */
  readonly roomVolume: number;
  /** As quatro faces do pilar, as três da viga (m²) — sempre, onde quer que a peça esteja. */
  readonly facesByKind: Readonly<Record<StructuralElementKind, number>>;
  /** A área de fundo da viga (m²). O pilar devolve zero. */
  readonly bottomByKind: Readonly<Record<StructuralElementKind, number>>;
  /** A altura somada dos pilares, o comprimento somado das vigas (m). */
  readonly runByKind: Readonly<Record<StructuralElementKind, number>>;
  /** Quantos pilares, quantas vigas (un). */
  readonly countByKind: Readonly<Record<StructuralElementKind, number>>;
  /** Área, quantidade, contorno e largura somada de cada tipo de vão. */
  readonly openingsByKind: Readonly<Record<OpeningKind, OpeningTotals>>;
  /** O guarda-corpo — metro, metro quadrado e peça (RN-268). */
  readonly railing: RailingMeasure;
  /** O shaft: área, requadro e quantos (RN-270). Sem shaft desenhado, zero em tudo. */
  readonly shaft: ShaftTotals;
  /**
   * A estaca: contagem, volume, comprimento, aço por bitola e marcadas por serviço (Fase 27).
   * Só a posicionada entra (RN-364), e nenhuma outra peça a soma (RN-360).
   */
  readonly pile: PileTotals;
  /** O bloco: contagem, volume, faces laterais e fundo (Fase 27, etapa 2). Nenhuma outra peça o soma (RN-360). */
  readonly pileCap: PileCapTotals;
  /** O baldrame, pelo trecho livre: contagem, comprimento, volume, faces e fundo (RN-368). */
  readonly groundBeam: GroundBeamTotals;
  /** O radier: contagem, área, volume, borda e fundo + borda (Fase 27, etapa 3). Nenhuma outra peça o soma (RN-360). */
  readonly raft: SlabTotals;
  /** A laje, sem os shafts (RN-370): contagem, área, volume, borda e fundo + borda. */
  readonly slab: SlabTotals;
  /** O muro de contenção: contagem, comprimento, face e volume, sem os pilares encaixados (RN-372). */
  readonly retainingWall: RetainingWallTotals;
  /** O pilar de contenção: contagem, altura, faces e volume, sem as faces dentro do muro (RN-372). */
  readonly retainingColumn: RetainingColumnTotals;
  /** A escada: contagem, projeção, volume, fôrma e degraus (F17). */
  readonly stair: StairTotals;
}

/**
 * Os totais todos em zero — o desenho que ainda não mediu nada.
 *
 * É o ponto de partida de quem monta um `GeometryTotals` à mão (um teste, uma tela sem
 * tipologia) e só quer dizer os campos que importam: o tipo cresce a cada fatia, e um literal
 * completo em cada lugar que o constrói cresceria junto.
 */
export function emptyTotals(): GeometryTotals {
  const porPeca = (): Record<StructuralElementKind, number> => ({ PILAR: 0, VIGA: 0 });
  const vao = (): OpeningTotals => ({ area: 0, count: 0, contour: 0, width: 0 });
  return {
    wallArea: 0,
    faceArea: 0,
    floorArea: 0,
    ceilingArea: 0,
    perimeter: 0,
    skirtingLength: 0,
    outsideFaceArea: 0,
    volumeByKind: porPeca(),
    espalaByKind: porPeca(),
    wallLength: 0,
    wallVolume: 0,
    wallCount: 0,
    roomCount: 0,
    roomVolume: 0,
    facesByKind: porPeca(),
    bottomByKind: porPeca(),
    runByKind: porPeca(),
    countByKind: porPeca(),
    openingsByKind: { PORTA: vao(), JANELA: vao(), PASSAGEM: vao() },
    railing: { length: 0, area: 0, count: 0 },
    shaft: NO_SHAFTS,
    pile: NO_PILES,
    pileCap: NO_PILE_CAPS,
    groundBeam: NO_GROUND_BEAMS,
    raft: NO_SLABS,
    slab: NO_SLABS,
    retainingWall: NO_RETAINING_WALLS,
    retainingColumn: NO_RETAINING_COLUMNS,
    stair: NO_STAIRS,
  };
}

/**
 * Os totais do desenho, multiplicados pelos andares que o repetem.
 *
 * É a conta que o engenheiro faz de cabeça e erra: **tipologia × pavimentos tipo**. O
 * desenho existe uma vez, o prédio tem vinte andares, e o total é o desenho vezes vinte —
 * sem que nada tenha sido lançado vinte vezes (RN-167).
 */
export function summarizeGeometry(geometry: TakeoffGeometry, times = 1): GeometryTotals {
  const sum = (values: readonly number[]): number =>
    roundQuantity(
      values.reduce((total, value) => total + value, 0),
      GEOMETRY_DECIMALS,
    );

  const scale = (value: number): number => roundQuantity(value * times, GEOMETRY_DECIMALS);

  return {
    wallArea: scale(geometry.wallArea),
    faceArea: scale(sum(geometry.rooms.map((room) => room.faceArea))),
    floorArea: scale(sum(geometry.rooms.map((room) => room.floorArea))),
    ceilingArea: scale(sum(geometry.rooms.map((room) => room.ceilingArea))),
    perimeter: scale(sum(geometry.rooms.map((room) => room.perimeter))),
    skirtingLength: scale(sum(geometry.rooms.map((room) => room.skirtingLength))),
    outsideFaceArea: scale(geometry.outsideFaceArea),
    // O volume escala na casa dele: duas casas comeriam a terceira, que é onde o metro
    // cúbico mora (ver `VOLUME_DECIMALS`).
    volumeByKind: {
      PILAR: roundQuantity(geometry.volumeByKind.PILAR * times, VOLUME_DECIMALS),
      VIGA: roundQuantity(geometry.volumeByKind.VIGA * times, VOLUME_DECIMALS),
    },
    // A espala é linear e grande, como o perímetro: duas casas bastam.
    espalaByKind: {
      PILAR: scale(geometry.espalaByKind.PILAR),
      VIGA: scale(geometry.espalaByKind.VIGA),
    },
    wallLength: scale(geometry.wallLength),
    // O volume escala na casa dele, pelo mesmo motivo do concreto.
    wallVolume: roundQuantity(geometry.wallVolume * times, VOLUME_DECIMALS),
    wallCount: scale(geometry.wallCount),
    roomCount: scale(geometry.rooms.length),
    roomVolume: roundQuantity(
      sum(geometry.rooms.map((room) => room.volume)) * times,
      VOLUME_DECIMALS,
    ),
    facesByKind: {
      PILAR: scale(geometry.facesByKind.PILAR),
      VIGA: scale(geometry.facesByKind.VIGA),
    },
    bottomByKind: {
      PILAR: scale(geometry.bottomByKind.PILAR),
      VIGA: scale(geometry.bottomByKind.VIGA),
    },
    runByKind: { PILAR: scale(geometry.runByKind.PILAR), VIGA: scale(geometry.runByKind.VIGA) },
    countByKind: {
      PILAR: scale(geometry.countByKind.PILAR),
      VIGA: scale(geometry.countByKind.VIGA),
    },
    openingsByKind: {
      PORTA: scaleOpening(geometry.openingsByKind.PORTA, scale),
      JANELA: scaleOpening(geometry.openingsByKind.JANELA, scale),
      PASSAGEM: scaleOpening(geometry.openingsByKind.PASSAGEM, scale),
    },
    // A peça também: cada andar tem a varanda dele, com o guarda-corpo dela.
    railing: {
      length: scale(geometry.railing.length),
      area: scale(geometry.railing.area),
      count: scale(geometry.railing.count),
    },
    // O shaft é prumada: vinte andares são vinte buracos, um em cima do outro.
    shaft: {
      area: scale(geometry.shaft.area),
      contour: scale(geometry.shaft.contour),
      count: geometry.shaft.count * times,
    },
    // A estaca de cada pavimento que repete o desenho conta de novo, como o shaft.
    pile: scalePileTotals(geometry.pile, times),
    // O bloco e o baldrame de cada pavimento que repete o desenho contam de novo, como a estaca.
    pileCap: scalePileCapTotals(geometry.pileCap, times),
    groundBeam: scaleGroundBeamTotals(geometry.groundBeam, times),
    // O radier e a laje de cada pavimento que repete o desenho contam de novo, como o bloco.
    raft: scaleSlabTotals(geometry.raft, times),
    slab: scaleSlabTotals(geometry.slab, times),
    // O muro e o pilar de contenção de cada pavimento que repete o desenho contam de novo.
    retainingWall: scaleRetainingWallTotals(geometry.retainingWall, times),
    retainingColumn: scaleRetainingColumnTotals(geometry.retainingColumn, times),
    // A escada de cada pavimento que repete o desenho conta de novo.
    stair: scaleStairTotals(geometry.stair, times),
  };
}

function scaleOpening(total: OpeningTotals, scale: (value: number) => number): OpeningTotals {
  return {
    area: scale(total.area),
    count: scale(total.count),
    contour: scale(total.contour),
    width: scale(total.width),
  };
}

/**
 * Como o desenho responde a uma medida: o número que sai dos totais, ou nada.
 *
 * Recebe o serviço também, e quase nenhuma leitura o usa: o total é da geometria, e o que é do
 * serviço fica fora dela. A exceção é a margem da soleira e do peitoril (RN-308) — o vão é do
 * desenho, os centímetros que a pedra passa dele são do serviço, e a soma dos dois mora aqui.
 */
type Leitura = (totals: GeometryTotals, service: WithSideMargin) => number | undefined;

/** O que pilar e viga medem em comum — a peça entra como chave, e nunca se soma (RN-257). */
function medidasDaPeca(peca: StructuralElementKind): Readonly<Partial<Record<MeasureBy, Leitura>>> {
  return {
    VOLUME: (totals) => totals.volumeByKind[peca],
    ESPALA: (totals) => totals.espalaByKind[peca],
    FACE: (totals) => totals.facesByKind[peca],
    COUNT: (totals) => totals.countByKind[peca],
    LENGTH: (totals) => totals.runByKind[peca],
    ACO: (totals, service) => acoDaPeca(totals.volumeByKind[peca], service),
  };
}

/**
 * O aço da peça: o volume dela vezes a taxa da disciplina, todas as bitolas somadas (RN-367).
 *
 * A taxa vem pendurada no serviço (`withSteelRate`), e não nos totais: o desenho sabe o
 * volume, e quantos kg vão em cada metro cúbico é decisão de quem calcula a estrutura desta
 * obra. Sem taxa — ausente ou zero — não há conta: `undefined`, e não zero, porque zero diria
 * que a peça não leva aço, e ela leva; só ainda ninguém disse quanto.
 */
function acoDaPeca(volume: number, service: WithSideMargin): number | undefined {
  const taxa = service.steelRatePerM3;
  if (taxa === undefined || !Number.isFinite(taxa) || taxa <= 0) return undefined;
  return roundQuantity(volume * taxa, GEOMETRY_DECIMALS);
}

/** O que porta, janela e passagem medem em comum — a conta do contorno já vem feita por tipo. */
function medidasDoVao(tipo: OpeningKind): Readonly<Partial<Record<MeasureBy, Leitura>>> {
  return {
    AREA: (totals) => totals.openingsByKind[tipo].area,
    COUNT: (totals) => totals.openingsByKind[tipo].count,
    CONTOUR: (totals) => totals.openingsByKind[tipo].contour,
    // Σ (largura + 2 × margem) × quantos = a largura somada + 2 × margem × quantos (RN-308).
    // A contagem já leva o `count` de cada registro e os andares do prédio, como a largura.
    WIDTH: (totals, service) => {
      const vao = totals.openingsByKind[tipo];
      const margem = countedSideMargin(service);
      if (margem === 0) return vao.width;
      return roundQuantity(vao.width + 2 * margem * vao.count, GEOMETRY_DECIMALS);
    },
  };
}

/**
 * A conta de cada par — o que `totalFor` lê (ADR-148).
 *
 * É a metade de `MEASURES_BY_ELEMENT` que diz **de onde** sai o número: a tabela de pares diz
 * que o par existe, e esta diz em que campo dos totais ele mora. A mesma grandeza lê campos
 * diferentes conforme a peça — "faces" são dois lados na parede, quatro no pilar e três na viga —,
 * e é por ser uma tabela, e não um `switch` sobre a grandeza, que o elemento novo entra com uma
 * linha e nenhuma das outras se mexe. Elemento que o desenho não mede (`NENHUM`) fica vazio, e o
 * `Record` obriga a dizê-lo.
 *
 * O teste da matriz cobra que todo par da tabela de pares tenha leitura aqui: um par sem ela
 * mediria "nada" calado, que é o defeito que a fatia 4 fechou.
 */
const LEITURA_DO_PAR: Readonly<Record<AppliesTo, Readonly<Partial<Record<MeasureBy, Leitura>>>>> = {
  PAREDE: {
    WALL: (totals) => totals.wallArea,
    FACE: (totals) => totals.faceArea,
    LENGTH: (totals) => totals.wallLength,
    VOLUME: (totals) => totals.wallVolume,
    COUNT: (totals) => totals.wallCount,
  },
  AMBIENTE: {
    FLOOR: (totals) => totals.floorArea,
    CEILING: (totals) => totals.ceilingArea,
    PERIMETER: (totals) => totals.perimeter,
    SKIRTING: (totals) => totals.skirtingLength,
    COUNT: (totals) => totals.roomCount,
    // As paredes do ambiente: a face que cada ambiente encara, sem os vãos — a soma de
    // `RoomGeometry.faceArea` (RN-178), o mesmo número das faces da parede no total.
    FACE: (totals) => totals.faceArea,
    VOLUME: (totals) => totals.roomVolume,
  },
  PILAR: medidasDaPeca('PILAR'),
  VIGA: {
    ...medidasDaPeca('VIGA'),
    // O pilar é vertical e não tem fundo: a área de fundo é da viga, e só dela.
    BOTTOM: (totals) => totals.bottomByKind.VIGA,
  },
  PORTA: medidasDoVao('PORTA'),
  JANELA: medidasDoVao('JANELA'),
  PASSAGEM: medidasDoVao('PASSAGEM'),
  // O do lado sem parede e o de cima da mureta, juntos (RN-268): metro, metro quadrado e peça.
  GUARDA_CORPO: {
    LENGTH: (totals) => totals.railing.length,
    AREA: (totals) => totals.railing.area,
    COUNT: (totals) => totals.railing.count,
  },
  // A peça mede a si mesma (RN-270): o buraco, o requadro e quantos — já com o `count` de cada
  // registro. O piso e o teto a menos são do ambiente, e não daqui (RN-271).
  SHAFT: {
    AREA: (totals) => totals.shaft.area,
    CONTOUR: (totals) => totals.shaft.contour,
    COUNT: (totals) => totals.shaft.count,
  },
  // A estaca mede a si mesma, e só a posicionada (RN-364). O aço é o dos **tipos** das estacas
  // desenhadas, e não volume × taxa da disciplina (RN-367). As marcadas são as **deste**
  // serviço (RN-365): sem a chave dele a leitura não sabe qual é, e não mede — somar as marcas
  // de todos os serviços daria ao teste de resistência as do controle tecnológico.
  ESTACA: {
    ACO: (totals) => pileSteelTotal(totals.pile),
    VOLUME: (totals) => totals.pile.volume,
    LENGTH: (totals) => totals.pile.length,
    COUNT: (totals) => totals.pile.count,
    MARKED: (totals, service) =>
      service.key === undefined ? undefined : (totals.pile.marked[service.key] ?? 0),
  },
  // O bloco mede a si mesmo (D3). O aço é o volume vezes a taxa da disciplina — como o pilar e a
  // viga, e ao contrário da estaca, cujo aço vem do tipo (RN-367).
  BLOCO: {
    ACO: (totals, service) => acoDaPeca(totals.pileCap.volume, service),
    VOLUME: (totals) => totals.pileCap.volume,
    FACE: (totals) => totals.pileCap.faces,
    BOTTOM: (totals) => totals.pileCap.bottom,
    COUNT: (totals) => totals.pileCap.count,
  },
  // O baldrame mede o trecho livre (RN-368): volume, faces, fundo e comprimento já descontam o
  // que passa dentro dos blocos, e o aço sai do volume livre.
  BALDRAME: {
    ACO: (totals, service) => acoDaPeca(totals.groundBeam.volume, service),
    VOLUME: (totals) => totals.groundBeam.volume,
    FACE: (totals) => totals.groundBeam.faces,
    BOTTOM: (totals) => totals.groundBeam.bottom,
    LENGTH: (totals) => totals.groundBeam.length,
    COUNT: (totals) => totals.groundBeam.count,
  },
  // O radier mede o contorno inteiro, a borda e o concreto de cada região (RN-369). O aço é o
  // volume vezes a taxa da disciplina (RN-367). O shaft não o fura (RN-370).
  RADIER: {
    ACO: (totals, service) => acoDaPeca(totals.raft.volume, service),
    VOLUME: (totals) => totals.raft.volume,
    AREA: (totals) => totals.raft.area,
    CONTOUR: (totals) => totals.raft.edge,
    COUNT: (totals) => totals.raft.count,
  },
  // A laje mede o mesmo, e o que o shaft ocupa já saiu da área e do volume (RN-370). A fôrma dela
  // é o fundo mais a borda, num serviço só.
  LAJE: {
    ACO: (totals, service) => acoDaPeca(totals.slab.volume, service),
    VOLUME: (totals) => totals.slab.volume,
    AREA: (totals) => totals.slab.area,
    CONTOUR: (totals) => totals.slab.edge,
    AREA_CONTOUR: (totals) => totals.slab.areaAndEdge,
    COUNT: (totals) => totals.slab.count,
  },
  // O muro mede a face em elevação, sem os pilares encaixados (RN-372); o aço é o volume vezes a
  // taxa da disciplina (RN-367).
  MURO_CONTENCAO: {
    ACO: (totals, service) => acoDaPeca(totals.retainingWall.volume, service),
    VOLUME: (totals) => totals.retainingWall.volume,
    FACE: (totals) => totals.retainingWall.face,
    LENGTH: (totals) => totals.retainingWall.length,
    COUNT: (totals) => totals.retainingWall.count,
  },
  // O pilar de contenção mede a fôrma (as faces sem as dentro do muro), o concreto inteiro e a
  // altura somada.
  PILAR_CONTENCAO: {
    ACO: (totals, service) => acoDaPeca(totals.retainingColumn.volume, service),
    VOLUME: (totals) => totals.retainingColumn.volume,
    FACE: (totals) => totals.retainingColumn.faces,
    LENGTH: (totals) => totals.retainingColumn.height,
    COUNT: (totals) => totals.retainingColumn.count,
  },
  // A escada mede a fôrma inteira, a projeção que o escoramento cobre e o concreto; o aço é o
  // volume vezes a taxa da disciplina (RN-367).
  ESCADA: {
    ACO: (totals, service) => acoDaPeca(totals.stair.volume, service),
    VOLUME: (totals) => totals.stair.volume,
    FACE: (totals) => totals.stair.formwork,
    AREA: (totals) => totals.stair.projection,
    COUNT: (totals) => totals.stair.count,
  },
  // Serviço por unidade não sai da geometria: quantas tomadas tem o apartamento é decisão de
  // projeto, e o que veio do modelo já chegou com o número dentro.
  NENHUM: {},
};

/**
 * A quantidade que o desenho determina para um serviço que mede assim.
 *
 * É o par de `measuredArea`, um nível acima: aquela responde por ambiente, esta pelo
 * conjunto — que é a pergunta do orçamento, onde o serviço é lançado uma vez para a
 * tipologia inteira.
 *
 * `UNIT` devolve `undefined` de propósito, e não zero: quantas tomadas tem o apartamento
 * é decisão de projeto, não consequência das medidas. Zero diria que o desenho respondeu
 * "nenhuma", e ele não respondeu nada.
 *
 * Recebe o **serviço**, e não só a grandeza, porque a grandeza sozinha não diz mais de onde
 * o número sai: volume é de pilar **ou** de viga (RN-257), e faces são de quatro peças. O
 * serviço sem peça escolhida devolve `undefined` pela mesma razão do `UNIT` — ele não mede
 * nada, e nunca a soma das duas (D17).
 *
 * A margem do serviço entra só na largura do vão (RN-308): a soleira de duas portas de 0,80 com
 * 5 cm de cada lado é 1,80 m, e não 1,60.
 */
export function totalFor(service: WithSideMargin, totals: GeometryTotals): number | undefined {
  const elemento = serviceElement(service);
  if (elemento === undefined) return undefined;
  return LEITURA_DO_PAR[elemento][service.measureBy]?.(totals, service);
}
